/**
 * Application Configuration Management
 * Simplified JavaScript version for immediate compatibility
 */

// Default configuration
const defaultConfig = {
  // Environment
  environment: 'development',
  version: '1.0.0',

  // API Configuration
  api: {
    baseUrl: '/api',
    timeout: 10000,
    retryAttempts: 3,
    retryDelay: 1000,
  },

  // Feature Flags
  features: {
    enablePWA: true,
    enableI18n: true,
    enableTryOn: true,
    enablePayments: false,
    enableNotifications: true,
    enablePerformanceMonitoring: true,
    enableAccessibility: true,
    enableResponsiveDesign: true,
  },

  // Performance Thresholds
  performance: {
    lcp: { good: 2500, poor: 4000 },
    fid: { good: 100, poor: 300 },
    cls: { good: 0.1, poor: 0.25 },
    fcp: { good: 1800, poor: 3000 },
    ttfb: { good: 800, poor: 1800 },
  },

  // Internationalization
  i18n: {
    defaultLanguage: 'en',
    supportedLanguages: ['en', 'zh', 'cn'],
    fallbackLanguage: 'en',
    enableRTL: true,
  },

  // PWA Configuration
  pwa: {
    name: 'FitCraft Studio',
    shortName: 'FitCraft',
    description: 'AI Virtual Try-On Platform',
    themeColor: '#000000',
    backgroundColor: '#000000',
  },
};

/**
 * Configuration Manager Class
 */
class ConfigManager {
  constructor() {
    this.config = { ...defaultConfig };
    this.initialized = false;
  }

  /**
   * Initialize configuration
   */
  init() {
    if (this.initialized) {
      return this.config;
    }

    // Detect environment
    this.config.environment = this.detectEnvironment();

    // Apply environment-specific overrides
    this.applyEnvironmentConfig();

    this.initialized = true;
    return this.config;
  }

  /**
   * Detect current environment
   */
  detectEnvironment() {
    // Check for explicit environment variables
    if (typeof window !== 'undefined') {
      if (window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1' ||
          window.location.hostname.includes('dev')) {
        return 'development';
      }

      if (window.location.hostname.includes('test') ||
          window.location.hostname.includes('staging')) {
        return 'test';
      }
    }

    return 'production';
  }

  /**
   * Apply environment-specific configuration
   */
  applyEnvironmentConfig() {
    const env = this.config.environment;

    // Load environment variables
    this.loadEnvironmentVariables();

    if (env === 'development') {
      // Development overrides
      this.config.features.enablePayments = false;
      this.config.performance.lcp.good = 3000;
    } else if (env === 'production') {
      // Production overrides
      this.config.features.enablePayments = true;
      this.config.api.timeout = 15000;
    }
  }

  /**
   * Load environment variables
   */
  loadEnvironmentVariables() {
    // Helper function to get environment variables
    const getEnvVar = (key, fallback = null) => {
      // Check window.ENV (CloudFlare Pages via /api/env)
      if (typeof window !== 'undefined' && window.ENV && window.ENV[key]) {
        return window.ENV[key];
      }

      // Check window.env (alternative naming)
      if (typeof window !== 'undefined' && window.env && window.env[key]) {
        return window.env[key];
      }

      // Check window.RUNTIME_ENV (alternative)
      if (typeof window !== 'undefined' && window.RUNTIME_ENV && window.RUNTIME_ENV[key]) {
        return window.RUNTIME_ENV[key];
      }

      // Check window.FITCRAFT_CONFIG
      if (typeof window !== 'undefined' && window.FITCRAFT_CONFIG && window.FITCRAFT_CONFIG[key]) {
        return window.FITCRAFT_CONFIG[key];
      }

      // Check window.FitCraftConfig
      if (typeof window !== 'undefined' && window.FitCraftConfig && window.FitCraftConfig[key]) {
        return window.FitCraftConfig[key];
      }

      return fallback;
    };

    // Add Supabase configuration
    this.config.SUPABASE_URL = getEnvVar('SUPABASE_URL');
    this.config.SUPABASE_ANON_KEY = getEnvVar('SUPABASE_ANON_KEY');

    // Add other environment-specific configs
    this.config.SENTRY_DSN = getEnvVar('SENTRY_DSN');
    this.config.POSTHOG_API_KEY = getEnvVar('POSTHOG_API_KEY');
    this.config.POSTHOG_HOST = getEnvVar('POSTHOG_HOST', 'https://api.posthog.com');
  }

  /**
   * Get full configuration
   */
  getConfig() {
    if (!this.initialized) {
      this.init();
    }
    return this.config;
  }

  /**
   * Get configuration section
   */
  get(section) {
    const config = this.getConfig();
    return config[section] || null;
  }

  /**
   * Check if feature is enabled
   */
  isFeatureEnabled(featureName) {
    const features = this.get('features');
    return features ? features[featureName] === true : false;
  }

  /**
   * Environment checks
   */
  isDevelopment() {
    return this.getConfig().environment === 'development';
  }

  isProduction() {
    return this.getConfig().environment === 'production';
  }

  isTest() {
    return this.getConfig().environment === 'test';
  }

  /**
   * Get API URL
   */
  getApiUrl(endpoint = '') {
    const api = this.get('api');
    return `${api.baseUrl}${endpoint}`;
  }

  /**
   * Get performance threshold
   */
  getPerformanceThreshold(metric) {
    const performance = this.get('performance');
    return performance ? performance[metric] : null;
  }
}

// Create singleton instance
export const configManager = new ConfigManager();

// Auto-initialize
configManager.init();